import { TicketEventAction, type TicketTimelineEntryDto } from "@/api/tickets";
import type { MessageValues, TranslationKey } from "@/locales/i18n";
import type { TicketEventSegment } from "./ticket-presentation";

type Translate = (key: TranslationKey, values?: MessageValues) => string;

const STATUS_STORAGE_KEYS: Record<string, TranslationKey> = {
  new: "tickets.status.new",
  open: "tickets.status.open",
  pending: "tickets.status.pending",
  resolved: "tickets.status.resolved",
  closed: "tickets.status.closed",
  cancelled: "tickets.status.cancelled",
};

const PRIORITY_STORAGE_KEYS: Record<string, TranslationKey> = {
  low: "tickets.priority.low",
  normal: "tickets.priority.normal",
  high: "tickets.priority.high",
  urgent: "tickets.priority.urgent",
};

const ACTION_ICONS: Record<string, string> = {
  [TicketEventAction.Created]: "tabler:ticket",
  [TicketEventAction.ContentUpdated]: "tabler:edit",
  [TicketEventAction.PriorityChanged]: "tabler:flag",
  [TicketEventAction.CategoryChanged]: "tabler:tag",
  [TicketEventAction.CustomerChanged]: "tabler:address-book",
  [TicketEventAction.Routed]: "tabler:route",
  [TicketEventAction.Assigned]: "tabler:user-check",
  [TicketEventAction.Unassigned]: "tabler:user-off",
  [TicketEventAction.StatusChanged]: "tabler:status-change",
  [TicketEventAction.ParticipantAdded]: "tabler:user-plus",
  [TicketEventAction.ParticipantRemoved]: "tabler:user-minus",
  [TicketEventAction.AttachmentAdded]: "tabler:paperclip",
  [TicketEventAction.TagAdded]: "tabler:bookmark",
  [TicketEventAction.TagRemoved]: "tabler:bookmark-off",
};

function entityName(displayName: string | null, rawValue: string | null): string | null {
  return displayName ?? rawValue ?? null;
}

function statusLabel(value: string | null, t: Translate): string {
  const key = value ? STATUS_STORAGE_KEYS[value] : undefined;
  return key ? t(key) : value || t("tickets.status.unknown");
}

function priorityLabel(value: string | null, t: Translate): string {
  const key = value ? PRIORITY_STORAGE_KEYS[value] : undefined;
  return key ? t(key) : value || t("tickets.priority.unknown");
}

export function ticketEventIcon(action: string | null): string {
  return (action && ACTION_ICONS[action]) || "tabler:circle-dot";
}

const TOKEN_PREFIX = "⟦i18n:";
const TOKEN_SUFFIX = "⟧";
const TOKEN_PATTERN = /(⟦i18n:[A-Za-z0-9_]+⟧)/g;

/**
 * Translate the complete sentence first, then split its named values back into
 * emphasized segments. This lets each locale own its word order instead of
 * assembling English from Chinese-oriented fragments.
 */
function translatedSegments(t: Translate, key: TranslationKey, values: Record<string, string> = {}): TicketEventSegment[] {
  const markedValues = Object.fromEntries(Object.keys(values).map((name) => [name, `${TOKEN_PREFIX}${name}${TOKEN_SUFFIX}`]));
  return t(key, markedValues)
    .split(TOKEN_PATTERN)
    .filter(Boolean)
    .map((part) => {
      if (!part.startsWith(TOKEN_PREFIX)) return { text: part };
      const name = part.slice(TOKEN_PREFIX.length, -TOKEN_SUFFIX.length);
      return { text: values[name] ?? "", emphasis: true };
    });
}

function actorKey(actor: string | undefined, actorKey: TranslationKey, systemKey: TranslationKey): TranslationKey {
  return actor ? actorKey : systemKey;
}

/** Event copy uses complete localized templates so Chinese and English can use their natural word order. */
export function describeTicketEvent(entry: TicketTimelineEntryDto, t: Translate): TicketEventSegment[] {
  const actor = entry.actorDisplayName?.trim();
  const from = entityName(entry.fromDisplayName, entry.fromValue);
  const to = entityName(entry.toDisplayName, entry.toValue);
  const actorValue: Record<string, string> = actor ? { actor } : {};

  switch (entry.action) {
    case TicketEventAction.Created:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.created.actor", "tickets.timeline.created.system"), actorValue);
    case TicketEventAction.ContentUpdated:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.contentUpdated.actor", "tickets.timeline.contentUpdated.system"), actorValue);
    case TicketEventAction.PriorityChanged:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.priorityChanged.actor", "tickets.timeline.priorityChanged.system"), {
        ...actorValue,
        from: priorityLabel(entry.fromValue, t),
        to: priorityLabel(entry.toValue, t),
      });
    case TicketEventAction.StatusChanged:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.statusChanged.actor", "tickets.timeline.statusChanged.system"), {
        ...actorValue,
        from: statusLabel(entry.fromValue, t),
        to: statusLabel(entry.toValue, t),
      });
    case TicketEventAction.CategoryChanged:
      return to
        ? translatedSegments(t, actorKey(actor, "tickets.timeline.categoryChanged.actor", "tickets.timeline.categoryChanged.system"), { ...actorValue, to })
        : translatedSegments(t, actorKey(actor, "tickets.timeline.categoryCleared.actor", "tickets.timeline.categoryCleared.system"), actorValue);
    case TicketEventAction.CustomerChanged:
      return to
        ? translatedSegments(t, actorKey(actor, "tickets.timeline.customerChanged.actor", "tickets.timeline.customerChanged.system"), { ...actorValue, to })
        : translatedSegments(t, actorKey(actor, "tickets.timeline.customerCleared.actor", "tickets.timeline.customerCleared.system"), actorValue);
    case TicketEventAction.Routed:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.routed.actor", "tickets.timeline.routed.system"), {
        ...actorValue,
        to: to ?? t("tickets.assignment.unknownTeam"),
      });
    case TicketEventAction.Assigned:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.assigned.actor", "tickets.timeline.assigned.system"), {
        ...actorValue,
        to: to ?? t("tickets.assignment.unknownMember"),
      });
    case TicketEventAction.Unassigned:
      return from
        ? translatedSegments(t, actorKey(actor, "tickets.timeline.unassignedFrom.actor", "tickets.timeline.unassignedFrom.system"), { ...actorValue, from })
        : translatedSegments(t, actorKey(actor, "tickets.timeline.unassigned.actor", "tickets.timeline.unassigned.system"), actorValue);
    case TicketEventAction.ParticipantAdded:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.participantAdded.actor", "tickets.timeline.participantAdded.system"), {
        ...actorValue,
        to: to ?? t("tickets.assignment.unknownMember"),
      });
    case TicketEventAction.ParticipantRemoved:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.participantRemoved.actor", "tickets.timeline.participantRemoved.system"), {
        ...actorValue,
        from: from ?? t("tickets.assignment.unknownMember"),
      });
    case TicketEventAction.AttachmentAdded:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.attachmentAdded.actor", "tickets.timeline.attachmentAdded.system"), actorValue);
    case TicketEventAction.TagAdded:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.tagAdded.actor", "tickets.timeline.tagAdded.system"), {
        ...actorValue,
        to: to ?? t("tickets.tags.unknown"),
      });
    case TicketEventAction.TagRemoved:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.tagRemoved.actor", "tickets.timeline.tagRemoved.system"), {
        ...actorValue,
        from: from ?? t("tickets.tags.unknown"),
      });
    default:
      return translatedSegments(t, actorKey(actor, "tickets.timeline.unknown.actor", "tickets.timeline.unknown.system"), {
        ...actorValue,
        action: entry.action ?? t("tickets.timeline.unknownAction"),
      });
  }
}
